/**
 * Shipping & Returns — the target of the footer's "Shipping & Returns" link.
 *
 * Every amount on this page is read from DEFAULT_PRICING_CONFIG, the same constant
 * src/lib/pricing-calculator.ts charges from, rather than typed in as prose. A published
 * policy that disagrees with the amount a customer is actually billed is worse than no
 * page at all, and hardcoding the figures here would guarantee that drift the first time
 * a fee changes. The durations come from siteConfig.shipping for the same reason.
 *
 * Scope is deliberately narrow: this states only what the code already enforces. Business
 * facts nobody has supplied are absent rather than invented -- see the TODO below.
 *
 * TODO (owner) -- each of these is a business fact, not a code decision, so it is omitted
 * rather than guessed at:
 *   - Courier partner(s), and whether a tracking link is emailed on dispatch.
 *   - Who pays return postage, and whether a pickup is arranged or the customer ships back.
 *   - Refund timeline once a return is received, and the refund route (original payment
 *     method vs store credit).
 *   - Whether exchanges are offered as distinct from refunds.
 *   - Order cut-off time for same-day dispatch, and non-working days.
 *   - Whether you ship outside India (the page currently states India only).
 * Add each as a section here, then follow the "To publish" note on the robots meta below.
 */

import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { formatPrice } from "@/components/forms/price-display";
import { DEFAULT_PRICING_CONFIG } from "@/lib/pricing-calculator";
import { useStoreShippingSettings } from "@/lib/store-settings";

export const Route = createFileRoute("/shipping")({
  head: () => ({
    meta: [
      { title: `Shipping, Returns & Cancellation Policy — ${siteConfig.name}` },
      {
        name: "description",
        content:
          `Delivery charges, dispatch times, GST, no-cancellation policy and ${siteConfig.shipping.returnDays}-day returns for Aster Decorz orders within India.`,
      },
      {
        property: "og:title",
        content: `Shipping & Returns Policy — ${siteConfig.shortName}`,
      },
      {
        property: "og:description",
        content: `Delivery charges, no cancellations, and ${siteConfig.shipping.returnDays}-day return details.`,
      },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/shipping` }],
  }),
  component: ShippingAndReturns,
});

function ShippingAndReturns() {
  const shippingSettings = useStoreShippingSettings();
  const { gstRate } = DEFAULT_PRICING_CONFIG;
  const standardShippingFee = shippingSettings.standardShippingFee;
  const expressShippingFee = shippingSettings.expressShippingFee;
  const freeShippingThreshold = shippingSettings.freeShippingThreshold;
  const standardDays = shippingSettings.standardDays;
  const expressDays = shippingSettings.expressDays;
  const { returnDays } = siteConfig.shipping;

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <div className="section-container">
        <Reveal>
          <p className="eyebrow">Support</p>
          <h1 className="mt-6 max-w-3xl">Shipping, Returns &amp; Policies</h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            What delivery costs, how long it takes, our return window, and cancellation policy. All orders ship within India.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <div className="space-y-10 lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-2xl">Delivery charges</h2>
              <dl className="mt-5 divide-y border-t">
                <div className="flex items-baseline justify-between gap-6 py-3">
                  <dt className="text-sm">
                    Standard delivery
                    <span className="block text-xs text-muted-foreground">
                      {standardDays} working days
                    </span>
                  </dt>
                  <dd className="font-display text-base tabular-nums">
                    {formatPrice(standardShippingFee)}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-6 py-3">
                  <dt className="text-sm">
                    Express delivery
                    <span className="block text-xs text-muted-foreground">
                      {expressDays} working days
                    </span>
                  </dt>
                  <dd className="font-display text-base tabular-nums">
                    {formatPrice(expressShippingFee)}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-6 py-3">
                  <dt className="text-sm">
                    Standard delivery on orders over {formatPrice(freeShippingThreshold)}
                    <span className="block text-xs text-muted-foreground">
                      Express is charged at {formatPrice(expressShippingFee)} whatever the order
                      value
                    </span>
                  </dt>
                  <dd className="font-display text-base">Free</dd>
                </div>
              </dl>
            </Reveal>

            <Reveal delay={100}>
              <h2 className="font-display text-2xl">GST</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Product prices shown on the site are all-inclusive of {Math.round(gstRate * 100)}%
                GST (MRP). Delivery charges are shown separately at checkout (free standard delivery
                on qualifying orders), so the total you confirm before payment is the exact amount charged.
              </p>
            </Reveal>

            <Reveal delay={160}>
              <h2 className="font-display text-2xl">Returns &amp; Refunds</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Pieces can be returned within {returnDays} days of delivery, provided they remain in their original crate
                and protective packaging. Every item is potted and shipped assembled, so keeping the crate
                until you have decided where a piece is going is recommended.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                To initiate a return, email{" "}
                <a
                  href={`mailto:${siteConfig.contact.email}?subject=Return%20request`}
                  className="text-primary underline underline-offset-4"
                >
                  {siteConfig.contact.email}
                </a>{" "}
                with your order number and photos of the item. Once the return is received and verified at our facility, refunds are processed to your original payment method within 5–7 business days.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <h2 className="font-display text-2xl">Cancellations</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                We do not accept order cancellations once an order has been placed and confirmed. All orders are automatically routed to our warehouse team for packaging and rapid dispatch.
              </p>
            </Reveal>

            <Reveal delay={240}>
              <h2 className="font-display text-2xl">Indoor Botanical Care</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                All botanicals are crafted for indoor aesthetic display. Keep away from sustained harsh direct sunlight or open rain, and dust occasionally with a dry soft cloth.
              </p>
            </Reveal>
          </div>

          <Reveal delay={120} className="lg:col-span-4 lg:col-start-9">
            <div className="border bg-card p-6">
              <h2 className="font-display text-lg">Something not covered here?</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Email us and you will get a real answer rather than a template.
              </p>
              <a
                href={`mailto:${siteConfig.contact.email}`}
                className="mt-5 inline-flex min-h-11 items-center bg-primary px-6 text-xs tracking-[0.2em] text-primary-foreground uppercase transition-opacity hover:opacity-90"
              >
                {siteConfig.contact.email}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
